import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchUser } from '../../invite-players/hooks';
import { API_URLS } from '../../consts';
import {
  BoxProps,
  ClickAwayListener,
  Popper,
  TextFieldProps,
} from '@mui/material';
import { BhabiFirestoreModel } from '../components/message';
import {
  collection,
  deleteDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { db } from '@/shared/lib';
import { useFirestoreQuery } from '../components';
import { useMyBlockedSet } from '../hooks';
import { deleteChatQuota } from '../hooks/use-chat-quota';
import {
  useChatThreads,
  deleteChatThreadSummary,
  upsertChatThreadSummary,
} from '../hooks/use-chat-threads';
import {
  ConfirmDeleteButton,
  ErrorLayout,
  UserAvatar,
} from '@/shared/components';
import { SearchUser } from '../../invite-players/components';

import { groupBy } from 'lodash';
import { API_SERVICE_URL } from '@/api';
import { useMutation } from 'react-query';
import axios, { AxiosError } from 'axios';
import { useSelector } from 'react-redux';
import { Store } from '@/redux';
import { LoggerService } from '@/shared/logger';
import { User } from '../../types';
import { useSearchParams } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import '../../components/social.css';

const SearchUserBar: FC<{
  textFieldProps?: TextFieldProps;
  onClickAvatar: DMPageProps['onClickThread'];
}> = ({ textFieldProps, onClickAvatar }) => {
  const { onChange, userInfo, noUserFound, isLoading, isError } = useSearchUser(
    `${API_URLS.SEARCH_USERS}?search=@&limit=20`,
    { limit: '20' }
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const searchRef = useRef<HTMLDivElement>(null);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    onChange(event);
    setAnchorEl(value.trim() ? event.currentTarget : null);
  };

  const closeResults = () => setAnchorEl(null);
  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeResults();
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      document
        .querySelector<HTMLButtonElement>('.bh-chat__search-hit')
        ?.focus();
    }
  };

  return (
    <div className="relative w-full">
      <SearchUser
        isLoading={isLoading}
        textFieldProps={{
          ref: searchRef,
          onChange: handleSearchChange,
          value: searchValue,
          onKeyDown: handleSearchKeyDown,
          onBlur: event => {
            const next = event.relatedTarget as Node | null;
            if (
              next &&
              !document
                .querySelector('.bh-chat__search-results')
                ?.contains(next)
            ) {
              closeResults();
            }
          },
          sx: { width: '100%' },
          ...textFieldProps,
        }}
      />
      {anchorEl && (
        <ClickAwayListener
          onClickAway={event => {
            if (!searchRef.current?.contains(event.target as Node))
              closeResults();
          }}
        >
          <Popper
            open
            anchorEl={anchorEl}
            placement="bottom-start"
            modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
            disablePortal
            style={{ zIndex: 1300, width: searchRef.current?.clientWidth }}
          >
            <div
              className="bh-chat__search-results"
              role="group"
              aria-label="Player search results"
            >
              {isError ? (
                <p className="bh-chat__search-empty" role="alert">
                  Could not search players. Try again.
                </p>
              ) : isLoading ? (
                <p className="bh-chat__search-empty" role="status">
                  Searching players…
                </p>
              ) : noUserFound ? (
                <p className="bh-chat__search-empty" role="status">
                  No players match that search.
                </p>
              ) : userInfo?.length ? (
                userInfo.map(user => (
                  <button
                    type="button"
                    key={user.id}
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => {
                      closeResults();
                      onClickAvatar(user.firebaseUID)();
                    }}
                    className="bh-chat__search-hit"
                    aria-label={`Message ${user.name}`}
                  >
                    <UserAvatar
                      {...{
                        photoURL: user.photoURL,
                        name: user.name,
                        hideState: true,
                        avatarProps: {
                          sx: { width: 36, height: 36, cursor: 'pointer' },
                        },
                      }}
                    />
                    <span className="bh-chat__search-name" title={user.name}>
                      {user.name}
                    </span>
                  </button>
                ))
              ) : null}
            </div>
          </Popper>
        </ClickAwayListener>
      )}
    </div>
  );
};

export const useGetFirebaseUsers = () => {
  const { token } = useSelector((state: Store) => state.userReducer);

  return useMutation<User[], AxiosError, string[]>({
    mutationKey: 'getFirebaseUsersByUIDs',
    mutationFn: async (ids: string[]) => {
      const url = [
        `${API_SERVICE_URL}${API_URLS.GET_USERS_BY_FIREBASE_UID}`,
        '?ids=',
        ids.join(','),
      ].join('');
      const res = await axios.get<User[]>(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      return res.data;
    },
  });
};

export const useDmPageQuery = (getOtherUserInfo = true) => {
  const { firebaseUID } = useSelector((state: Store) => state.userReducer);
  const { mutate: getFirebaseUsers, ...remaining } = useGetFirebaseUsers();

  const qc = useMemo(
    () =>
      !firebaseUID
        ? []
        : [
            orderBy('createdAt', 'desc'),
            // The DM sidebar derives the thread list by grouping the
            // most recent messages. If this cap is too low and one
            // thread dominates (e.g. someone sends a long burst), the
            // other threads silently drop off the sidebar because their
            // latest message is older than the cap. 200 covers the vast
            // majority of real users; a proper /chat_threads index doc
            // is the real fix and is tracked separately.
            limit(50),
            where('threadID', 'array-contains', firebaseUID),
          ],
    [firebaseUID]
  );

  const { messages } = useFirestoreQuery(qc);

  const pageData = useMemo(() => {
    if (!messages?.length || !firebaseUID) {
      return {
        uniqueMessagePerUser: [],
        threadWithIds: [],
      };
    }

    const thread = groupBy(messages, m => m.threadID.sort().toString());
    const threadWithIds = Object.keys(thread).map(id =>
      id
        .split(',')
        .filter(id => id !== firebaseUID)
        .join('')
    );

    if (getOtherUserInfo) {
      getFirebaseUsers(threadWithIds);
    }

    const uniqueMessagePerUser = Object.entries(thread).map(([, value]) => ({
      message: value[value.length - 1].message,
      isRead: value[value.length - 1].isRead,
      senderID: value[value.length - 1].senderID,
      receiverID: value[value.length - 1].receiverID,
      createdAt: value[value.length - 1].createdAt,
    }));

    const sortByTime =
      uniqueMessagePerUser.length > 1
        ? uniqueMessagePerUser.sort(
            (a, b) => b.createdAt?.seconds - a.createdAt?.seconds
          )
        : uniqueMessagePerUser;

    return { uniqueMessagePerUser: sortByTime, threadWithIds };
  }, [messages, firebaseUID, getFirebaseUsers, getOtherUserInfo]);

  return { pageData, firebaseUID, ...remaining };
};

const formatTimestamp = (ts?: Timestamp): string => {
  if (!ts?.seconds) return '';
  const date = new Date(ts.seconds * 1000);
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate();
  if (isYesterday) return 'Yesterday';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

interface DMPageProps {
  onClickThread: (id?: string) => () => void;
  pageContentWrapperProps?: BoxProps;
  textFieldProps?: TextFieldProps;
  activeThreadID?: string | null;
}

export const DMPage: FC<DMPageProps> = ({
  onClickThread,
  textFieldProps,
  activeThreadID,
}) => {
  const { firebaseUID } = useSelector((s: Store) => s.userReducer);
  const {
    threads,
    loading: threadsLoading,
    isError: threadsError,
  } = useChatThreads(100);

  // Fetch display info (name, photo) for every other-side participant.
  const { mutate: fetchUsers, data: userInfo } = useGetFirebaseUsers();
  useEffect(() => {
    if (!firebaseUID) return;
    const otherIDs = threads.map(t => t.otherUID).filter(Boolean);
    if (otherIDs.length) {
      fetchUsers(otherIDs);
    }
  }, [firebaseUID, threads, fetchUsers]);

  // Adapter so the existing JSX (which consumes `pageData` /
  // uniqueMessagePerUser) doesn't need a rewrite. One entry per
  // thread, sorted by lastMessageAt desc (server-side).
  const pageData = useMemo(
    () => ({
      uniqueMessagePerUser: threads.map(t => ({
        message: t.lastMessage,
        isRead: t.lastIsRead,
        senderID: t.lastSenderID,
        receiverID:
          t.lastSenderID === firebaseUID ? t.otherUID : firebaseUID || '',
        createdAt: t.lastMessageAt as Timestamp | undefined,
      })),
      threadWithIds: threads.map(t => t.otherUID),
    }),
    [threads, firebaseUID]
  );
  const data = userInfo;

  // One-time-per-session backfill of /chat_threads.
  //
  // Users who chatted before /chat_threads existed have no summary doc.
  // Naively this could be a single getDocs(limit:100) but that fails for
  // anyone whose recent activity has been concentrated in one busy thread
  // (the older threads' latest messages fall outside the window). We
  // paginate the /chat scan up to MAX_BACKFILL_PAGES * 100 messages,
  // deduping by sorted threadID key, and upsert any thread whose summary
  // doc we haven't already seeded.
  //
  // The backfill runs every page-load until the user has at least one
  // thread doc, AND additionally runs once per session as a safety net
  // so newly-discovered legacy threads (older than the active set's
  // recent messages) eventually surface.
  const backfillRanRef = useRef(false);
  useEffect(() => {
    if (!firebaseUID || threadsLoading || backfillRanRef.current) return;
    backfillRanRef.current = true;
    (async () => {
      const seenThreadKeys = new Set<string>();
      // Pre-populate seen with the threads we already have so we don't
      // overwrite up-to-date summaries with older content.
      for (const t of threads) {
        const key = [t.otherUID, firebaseUID].sort().join('|');
        seenThreadKeys.add(key);
      }

      const MAX_BACKFILL_PAGES = 5; // 5 * 100 = 500 messages worst-case
      const PAGE_SIZE = 100;
      let cursor: unknown = null;
      let pages = 0;

      try {
        while (pages < MAX_BACKFILL_PAGES) {
          const q = cursor
            ? query(
                collection(db, 'chat'),
                where('threadID', 'array-contains', firebaseUID),
                orderBy('createdAt', 'desc'),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                startAfter(cursor as any),
                limit(PAGE_SIZE)
              )
            : query(
                collection(db, 'chat'),
                where('threadID', 'array-contains', firebaseUID),
                orderBy('createdAt', 'desc'),
                limit(PAGE_SIZE)
              );

          const snap = await getDocs(q);
          if (snap.empty) break;

          let newThisPage = 0;
          snap.forEach(d => {
            const m = d.data() as BhabiFirestoreModel;
            if (!Array.isArray(m.threadID) || m.threadID.length !== 2) return;
            const key = [...m.threadID].sort().join('|');
            if (seenThreadKeys.has(key)) return;
            seenThreadKeys.add(key);
            newThisPage++;
            const other = m.threadID.find(u => u !== firebaseUID);
            if (!other || !m.senderID || !m.message) return;
            // Seed the thread summary. The lastMessage we use here is
            // the latest message *in this scan* for this thread — which,
            // because we ordered DESC, is the actual latest.
            upsertChatThreadSummary(m.senderID, other, m.message);
          });

          pages++;
          // If we made no progress on this page (everything was already
          // known), there's nothing left to discover going further back.
          if (newThisPage === 0 && pages > 1) break;
          cursor = snap.docs[snap.docs.length - 1];
        }
      } catch (err) {
        LoggerService.warn?.('chat-thread backfill failed', err);
      }
    })();
  }, [firebaseUID, threadsLoading, threads]);

  const isError = threadsError;
  const { blockedSet } = useMyBlockedSet();
  const [params, setSearchParams] = useSearchParams();
  // UIDs whose thread row should be hidden client-side after the user
  // pressed Delete, until the Firestore snapshot catches up. Prevents the
  // jarring spinner / list flash that happens during the round-trip.
  const [hiddenThreadUIDs, setHiddenThreadUIDs] = useState<Set<string>>(
    () => new Set()
  );

  // Once the Firestore snapshot stops carrying a hidden thread, drop it
  // from the local hidden set so any future re-created thread shows again.
  useEffect(() => {
    if (hiddenThreadUIDs.size === 0) return;
    const visibleUIDs = new Set(pageData.threadWithIds);
    setHiddenThreadUIDs(prev => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach(uid => {
        if (visibleUIDs.has(uid)) {
          next.add(uid);
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [pageData.threadWithIds, hiddenThreadUIDs.size]);

  if (!firebaseUID || isError) {
    return <ErrorLayout outerBoxProps={{ height: 'auto' }} />;
  }

  const onClickDeleteThread =
    (receiverID: string, senderID: string) => async () => {
      const chatCollection = collection(db, 'chat');

      // Optimistically hide the row immediately so deleting feels instant
      // — no spinner flash, no list collapse / re-render flicker. The
      // Firestore snapshot will catch up shortly after.
      if (receiverID) {
        setHiddenThreadUIDs(prev => {
          const next = new Set(prev);
          next.add(receiverID);
          return next;
        });
      }

      try {
        // The list rule on /chat requires request.query.limit <= 100, so
        // an unbounded query is denied. Cap at 100; a thread with more
        // messages is extremely rare and a second tap finishes the rest.
        const q1 = query(
          chatCollection,
          where('threadID', '==', [receiverID, senderID]),
          limit(100)
        );
        const q2 = query(
          chatCollection,
          where('threadID', '==', [senderID, receiverID]),
          limit(100)
        );

        const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);

        const deletions: Promise<void>[] = [];
        snap1.forEach(d => deletions.push(deleteDoc(d.ref)));
        snap2.forEach(d => deletions.push(deleteDoc(d.ref)));
        // Drop both directional chat quotas so a future re-start of the
        // thread requires a fresh entry fee.
        deletions.push(deleteChatQuota(receiverID, senderID));
        deletions.push(deleteChatQuota(senderID, receiverID));
        // Drop the per-thread summary doc so the sidebar updates instantly.
        deletions.push(deleteChatThreadSummary(receiverID, senderID));
        await Promise.all(deletions);
      } catch (err) {
        LoggerService.error('Failed to delete DM thread', err);
        // Roll back the optimistic hide if the delete failed so the user
        // can retry.
        setHiddenThreadUIDs(prev => {
          const next = new Set(prev);
          next.delete(receiverID);
          return next;
        });
        return;
      }

      const receiverIDInParams = params.get('receiverID');
      if (receiverIDInParams !== receiverID) {
        return;
      }

      setSearchParams(prev => {
        prev.delete('receiverID');
        return prev;
      });
    };

  return (
    <div className="bh-threads flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="bh-threads__head">
        <h1 className="bh-threads__title">Messages</h1>
        <SearchUserBar
          textFieldProps={textFieldProps}
          onClickAvatar={onClickThread}
        />
      </div>

      {/* Thread list */}
      <div className="bh-threads__list flex-1 overflow-y-auto overflow-x-hidden min-h-0">
        {threadsLoading && !pageData.uniqueMessagePerUser?.length ? (
          <ul
            className="bh-threads__ul"
            role="status"
            aria-label="Loading conversations"
          >
            {[0, 1, 2].map(i => (
              <li key={i} className="bh-skeleton-row" aria-hidden="true">
                <span />
                <span />
              </li>
            ))}
          </ul>
        ) : pageData.uniqueMessagePerUser?.length ? (
          <ul className="bh-threads__ul" aria-label="Conversations">
            {pageData.uniqueMessagePerUser.map((message, i) => {
              const isUnread =
                message.senderID !== firebaseUID && message.isRead === 'false';
              const otherUserID = pageData.threadWithIds.find(
                id => id === message.senderID || id === message.receiverID
              );
              const otherUser = data?.find(
                findUserById(message.receiverID, message.senderID)
              );
              const isActive = activeThreadID && otherUserID === activeThreadID;
              const preview =
                message.senderID === firebaseUID
                  ? `You: ${message.message}`
                  : message.message;
              // names arrive from a second request; never flash "Unknown"
              const displayName =
                otherUser?.name || (data ? 'Unknown user' : 'Loading…');
              const isBlocked = otherUserID
                ? blockedSet.has(otherUserID)
                : false;

              if (otherUserID && hiddenThreadUIDs.has(otherUserID)) {
                return null;
              }

              return (
                <li key={`${otherUserID ?? 'unknown'}-${i}`}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={onClickThread(otherUserID)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClickThread(otherUserID)();
                      }
                    }}
                    aria-current={isActive ? 'true' : undefined}
                    aria-label={`${displayName}${
                      isUnread ? ', unread' : ''
                    }: ${preview}`}
                    className="bh-thread"
                    data-active={!!isActive}
                    data-unread={isUnread}
                  >
                    <div className="relative flex-shrink-0">
                      <UserAvatar
                        {...{
                          name: otherUser?.name,
                          photoURL: otherUser?.photoURL || null,
                          hideState: true,
                          avatarProps: {
                            sx: { width: 44, height: 44 },
                          },
                        }}
                      />
                      {isUnread && (
                        <span className="bh-thread__dot" aria-hidden="true" />
                      )}
                    </div>

                    <div className="flex flex-col min-w-0" aria-hidden="true">
                      <div className="flex items-baseline gap-2 min-w-0">
                        <p className="bh-thread__name">{displayName}</p>
                        {isBlocked && (
                          <span
                            title="You blocked this user"
                            className="bh-thread__tag"
                          >
                            Blocked
                          </span>
                        )}
                        <span className="bh-thread__time">
                          {formatTimestamp(message.createdAt)}
                        </span>
                      </div>
                      <p className="bh-thread__preview">{preview}</p>
                    </div>

                    <div
                      className="flex-shrink-0"
                      onClick={e => e.stopPropagation()}
                    >
                      <ConfirmDeleteButton
                        onClick={onClickDeleteThread(
                          message.receiverID,
                          message.senderID
                        )}
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="bh-chat__empty" role="status">
            <div className="bh-chat__empty-inner">
              <div className="bh-chat__empty-icon" aria-hidden="true">
                ♥
              </div>
              <p className="bh-h3">No conversations yet</p>
              <p className="bh-muted bh-small">
                Search for a player above to start a chat.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function findUserById(receiverID: string, senderID: string) {
  return (user: User) =>
    user.firebaseUID === receiverID || user.firebaseUID === senderID;
}

// Re-export Box to keep prop-type compatibility for callers that still pass
// pageContentWrapperProps; it's now ignored by the new layout but consumers
// (e.g. legacy LgScreenChatPage code paths) won't break at the type level.
export type { BoxProps };
